import type { PayrollStatus, SalaryAutoSource, SalaryCalc, SalaryItemKind, TaxBracket, TaxMode, PayslipSheet } from '@mt/shared';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { StatusKind } from '@/design-system/status';

/** P21 B6 tính lương (docs/phases/P21.md §4.6, §9.5). Số tiền do server tính; màn hình chỉ hiển thị. */
export const PAY_KEY = ['payroll'] as const;

export const PAYROLL_STATUS_KIND: Record<PayrollStatus, StatusKind> = {
  draft: 'draft',
  calculated: 'processing',
  pending: 'pending',
  approved: 'approved',
};

export interface SalaryItemView {
  id: number;
  code: string;
  name: string;
  kind: SalaryItemKind;
  calc: SalaryCalc;
  autoSource: SalaryAutoSource | null;
  taxable: boolean;
  taxExemptCap: number | null;
  countsForInsurance: boolean;
  loseOnUnexcusedAbsence: boolean;
  formulaRate: number | null;
  formulaBaseCodes: string[];
  applyAll: boolean;
  defaultAmount: number;
  sortOrder: number;
  isActive: boolean;
  version: number;
}

export interface PeriodView {
  id: number;
  month: string;
  status: PayrollStatus;
  timesheetLocked: boolean;
  timesheetLockedAtCalc: boolean;
  paramsConfirmed: boolean;
  stdDays: number | null;
  /** PR 7: công chuẩn HR sửa cho kỳ (null = T2–T7 tự tính) */
  stdDaysOverride: number | null;
  stdDaysReason: string | null;
  /** Công chuẩn tự tính (T2–T7), chỉ có ở màn bảng lương */
  stdDaysDefault?: number;
  lineCount: number;
  totalNet: number;
  totalGross: number;
  totalEmployerCost: number;
  calculatedAt: string | null;
  calculatedByName: string | null;
  submittedAt: string | null;
  submittedByName: string | null;
  approvedAt: string | null;
  approvedByName: string | null;
  version: number;
}

export interface LineItemView {
  lineId: number;
  itemId: number | null;
  code: string;
  name: string;
  kind: SalaryItemKind;
  calc: SalaryCalc;
  autoSource: SalaryAutoSource | null;
  taxable: boolean;
  qty: number | null;
  rate: number | null;
  autoAmount: number;
  overrideAmount: number | null;
  overrideReason: string | null;
  overriddenByName: string | null;
  overriddenAt: string | null;
  amount: number;
}

export interface LineView {
  id: number;
  employeeId: number;
  employeeCode: string;
  fullName: string;
  departmentName: string | null;
  positionName: string | null;
  workGroup: string | null;
  /** DEC-354: khối theo phòng hiện tại */
  division: string | null;
  asiaStaffCode: string | null;
  contractSalary: number;
  insuranceSalary: number;
  dependants: number;
  taxMode: TaxMode;
  hasInsurance: boolean;
  stdDays: number;
  workDays: number;
  otHours: number;
  lateMinutes: number;
  unexcusedDays: number;
  /** PR 7: ngày lệnh Chủ nhật, giờ ngoài giờ TP xác nhận, thưởng cuối tháng */
  sundayDays: number;
  afterHours: number;
  bonus: number;
  gross: number;
  taxExempt: number;
  insuranceEmployee: number;
  taxableIncome: number;
  pit: number;
  deductions: number;
  net: number;
  insuranceEmployer: number;
  employerCost: number;
  detail: { ratio?: number; warnings?: string[]; driverOt?: number; driverLate?: number; driverTrip?: number; driverOrders?: number | null; insurance?: InsuranceDetail; driverNotes?: DriverNotes | null; sheet?: PayslipSheet | null };
  items: LineItemView[];
}

export interface InsuranceDetail {
  base: number;
  employee: { social: number; health: number; unemployment: number; total: number };
  employer: { social: number; health: number; unemployment: number; union: number; total: number };
}

export interface GridColumn {
  code: string;
  name: string;
  kind: SalaryItemKind;
  calc: SalaryCalc;
  sortOrder: number;
  autoSource: SalaryAutoSource | null;
}

export interface PeriodGrid {
  period: PeriodView;
  columns: GridColumn[];
  lines: LineView[];
  warnings: {
    timesheetLocked: boolean;
    timesheetLockedAtCalc: boolean;
    /** PR5 (DEC-208): đã "Duyệt tiền tài xế tháng" */
    driverPayApproved?: boolean;
    paramsConfirmed: boolean;
    paramsChangedSinceCalc: boolean;
    missingAsia: number;
    negativeNet: number;
    missingProfiles: { id: number; code: string; fullName: string }[];
  };
  /** PR 7: các bước của vòng lương, ai làm lúc nào */
  steps: PayrollStep[];
}

export interface PayrollStep {
  key: 'confirm' | 'signoff' | 'lock' | 'calculate' | 'submit' | 'approve';
  label: string;
  done: boolean;
  detail: string | null;
  byName: string | null;
  at: string | null;
  departments?: { id: number; name: string; signedByName: string | null; signedAt: string | null }[];
}

/** PR 7: thưởng cuối tháng (Admin / GĐ nhập) */
export interface BonusView {
  id: number;
  month: string;
  employeeId: number;
  employeeCode: string;
  fullName: string;
  departmentName: string | null;
  amount: number;
  reason: string;
  status: 'active' | 'cancelled';
  createdByName: string;
  createdAt: string;
  cancelledByName: string | null;
  cancelledAt: string | null;
  cancelReason: string | null;
}

export function useBonuses(month: string) {
  return useQuery({
    queryKey: [...PAY_KEY, 'bonuses', month],
    queryFn: () => api.get<{ month: string; items: BonusView[]; total: number }>('/hr/payroll-bonuses', { month }),
  });
}

export interface ParamView {
  id: number;
  effectiveFrom: string;
  empSocialRate: number;
  empHealthRate: number;
  empUnemploymentRate: number;
  erSocialRate: number;
  erHealthRate: number;
  erUnemploymentRate: number;
  erUnionRate: number;
  socialCap: number;
  unemploymentCap: number;
  selfDeduction: number;
  dependantDeduction: number;
  flatTaxRate: number;
  flatTaxThreshold: number;
  taxBrackets: TaxBracket[];
  note: string | null;
  confirmedAt: string | null;
  confirmedByName: string | null;
  version: number;
}

export interface SalaryEmployee {
  id: number;
  code: string;
  fullName: string;
  departmentName: string | null;
  workGroup: string;
  asiaStaffCode: string | null;
  profileFrom: string | null;
}

export interface ProfileView {
  id: number;
  employeeId: number;
  effectiveFrom: string;
  contractSalary: number;
  insuranceSalary: number | null;
  dependants: number;
  taxMode: TaxMode;
  hasInsurance: boolean;
  note: string | null;
  version: number;
  updatedByName: string | null;
}

export interface AssignmentView {
  id: number;
  itemId: number;
  itemCode: string;
  itemName: string;
  employeeId: number | null;
  departmentId: number | null;
  departmentName: string | null;
  amount: number;
  effectiveFrom: string;
  effectiveTo: string | null;
  note: string | null;
  version: number;
}

export interface EmployeeSalary {
  employee: { id: number; code: string; fullName: string; departmentId: number | null; departmentName: string | null; asiaStaffCode: string | null; positionName: string | null };
  profiles: ProfileView[];
  assignments: AssignmentView[];
  departmentAssignments: AssignmentView[];
}

export interface HistoryRow {
  id: number;
  action: string;
  summary: string;
  occurredAt: string;
  userName: string | null;
}

export interface PayslipListRow {
  id: number;
  month: string;
  gross: number;
  net: number;
  approvedAt: string;
}

/** DEC-768: ghi chú từng ngày tiền tài xế trên phiếu lương */
export interface DriverNotes {
  ot: string[];
  deduct: string[];
  trip: string[];
}
export type PayslipView = Omit<LineView, 'detail'> & { month: string; insurance: InsuranceDetail | null; driverNotes?: DriverNotes | null; sheet?: PayslipSheet | null };

export const usePeriods = (enabled = true) => useQuery({ queryKey: [...PAY_KEY, 'periods'], queryFn: () => api.get<PeriodView[]>('/hr/payroll-periods'), enabled });
export const usePeriodGrid = (id: number | null) =>
  useQuery({ queryKey: [...PAY_KEY, 'grid', id], queryFn: () => api.get<PeriodGrid>(`/hr/payroll-periods/${id}`), enabled: !!id });
export const usePeriodHistory = (id: number | null, enabled: boolean) =>
  useQuery({ queryKey: [...PAY_KEY, 'history', id], queryFn: () => api.get<HistoryRow[]>(`/hr/payroll-periods/${id}/history`), enabled: !!id && enabled });
export const useSalaryItems = (enabled = true) => useQuery({ queryKey: [...PAY_KEY, 'items'], queryFn: () => api.get<SalaryItemView[]>('/hr/salary-items'), enabled });
export const usePayrollParams = (enabled = true) => useQuery({ queryKey: [...PAY_KEY, 'params'], queryFn: () => api.get<ParamView[]>('/hr/payroll-parameters'), enabled });
export const useSalaryEmployees = (q: string, enabled = true) =>
  useQuery({ queryKey: [...PAY_KEY, 'employees', q], queryFn: () => api.get<SalaryEmployee[]>('/hr/salary-employees', { q: q || undefined }), enabled });
export const useEmployeeSalary = (id: number | null) =>
  useQuery({ queryKey: [...PAY_KEY, 'employee', id], queryFn: () => api.get<EmployeeSalary>(`/hr/employees/${id}/salary`), enabled: !!id });
export const useMyPayslips = (enabled = true) => useQuery({ queryKey: ['me', 'payslips'], queryFn: () => api.get<PayslipListRow[]>('/me/payslips'), enabled });
export const useMyPayslip = (id: number | null) =>
  useQuery({ queryKey: ['me', 'payslips', id], queryFn: () => api.get<PayslipView>(`/me/payslips/${id}`), enabled: !!id });

export const monthLabel = (m: string) => `${m.slice(5, 7)}/${m.slice(0, 4)}`;
export const pct = (v: number) => `${(v * 100).toLocaleString('vi-VN', { maximumFractionDigits: 2 })}%`;
